'use client'

import React, { useState, useEffect } from 'react'
import {
  Shield,
  AlertTriangle,
  Key,
  Mail,
  Phone,
  ArrowLeft
} from 'lucide-react'
import axiosReq from '@/app/interceptor'
import { Modal } from '@/app/components/modal/modal'
import OtpInput from '../hooks-and-utils/otp-input'
import { TwoFactorVerificationModalProps } from '@/app/interfaces/appInterface'
import { MessageAlert } from '@/app/components/message-alert'
import SpinnerButton from '@/app/components/spinner-button'
import Loading from '@/app/components/Loading'
import { useCountdownTimer } from '../hooks-and-utils/useCountdownTimer'
import { useOtpAutoSubmit } from '../hooks-and-utils/useOtpAutoSubmit'

const TwoFactorVerificationModal = ({
  isOpen,
  onClose,
  onSuccess,
  preAuthToken,
  email,
  action,
  purpose
}: TwoFactorVerificationModalProps) => {
  const [code, setCode] = useState('')
  const [error, setError] = useState('')
  const [useBackupCode, setUseBackupCode] = useState(false)
  const [showRecovery, setShowRecovery] = useState(false)
  const [recoveryMethod, setRecoveryMethod] = useState<'EMAIL' | 'SMS' | null>(
    null
  )
  const [recoveryCode, setRecoveryCode] = useState('')
  const [otpRequested, setOtpRequested] = useState(false)
  const [expiresIn, setExpiresIn] = useState<number>(0)
  const [isVerifying, setIsVerifying] = useState(false)
  const [isRequestingEmail, setIsRequestingEmail] = useState(false)
  const [isRequestingSMS, setIsRequestingSMS] = useState(false)
  const [isResending, setIsResending] = useState(false)
  const [isVerifyingRecovery, setIsVerifyingRecovery] = useState(false)
  const resendTimer = useCountdownTimer(otpRequested ? 60 : 0, otpRequested)
  const expiryTime = useCountdownTimer(expiresIn, otpRequested && expiresIn > 0)
  const isProcessing =
    isVerifying ||
    isRequestingEmail ||
    isRequestingSMS ||
    isResending ||
    isVerifyingRecovery
  const isLoginDisabled = purpose === 'login' && !preAuthToken

  // Reset all state when modal closes.
  useEffect(() => {
    if (!isOpen) {
      setCode('')
      setError('')
      setUseBackupCode(false)
      setShowRecovery(false)
      setRecoveryMethod(null)
      setRecoveryCode('')
      setOtpRequested(false)
      setExpiresIn(0)
      setIsVerifying(false)
      setIsRequestingEmail(false)
      setIsRequestingSMS(false)
      setIsResending(false)
      setIsVerifyingRecovery(false)
    }
  }, [isOpen])

  useEffect(() => {
    if (purpose === 'login' && isOpen && !preAuthToken) {
      setError('Session expired! Please log in again.')
      console.error(
        'TwoFactorVerificationModal: preAuthToken is required for login flow'
      )
    }
  }, [purpose, isOpen, preAuthToken])

  // Login OTP — clears code on failure to stop auto-submit loop.
  useOtpAutoSubmit({
    value: code,
    enabled: isOpen && !useBackupCode && !otpRequested,
    isProcessing: isVerifying,
    onSubmit: async () => {
      const success = await handleSubmit()
      if (!success) {
        setCode('')
      }
    }
  })

  // Recovery OTP — clears recoveryCode on failure to stop auto-submit loop.
  useOtpAutoSubmit({
    value: recoveryCode,
    enabled: isOpen && otpRequested,
    isProcessing: isVerifyingRecovery,
    onSubmit: async () => {
      const success = await handleVerifyRecovery()
      if (!success) {
        setRecoveryCode('')
      }
    }
  })

  const handleSubmit = async (e?: React.FormEvent): Promise<boolean> => {
    e?.preventDefault()

    if (isVerifying) return false

    if (purpose === 'login' && !preAuthToken) {
      setError('Session expired! Please log in again.')
      return false
    }

    if (!code || (useBackupCode ? code.length < 14 : code.length < 6)) {
      setError(
        useBackupCode
          ? 'Please enter a valid backup code'
          : 'Please enter a valid 6-digit code'
      )
      return false
    }

    setIsVerifying(true)
    setError('')

    try {
      if (purpose === 'login') {
        const { data, status } = await axiosReq.post(
          '/app/v2/auth/2fa/verify2fa',
          { code, type: useBackupCode ? 'BACKUP' : 'TOTP' },
          { headers: { Authorization: `Bearer ${preAuthToken}` } }
        )

        if (status === 200) {
          const token = data.data?.token || data.token
          const requiresAuthenticatorSetup =
            data.data?.requiresAuthenticatorSetup ?? false

          if (token) {
            localStorage.setItem('token', token)
            localStorage.removeItem('preAuthToken')
            const tempUser = localStorage.getItem('tempUser')
            if (tempUser) localStorage.setItem('uo', tempUser)
            localStorage.removeItem('tempUser')
            onSuccess(token, undefined, requiresAuthenticatorSetup)
            onClose()
            return true
          } else {
            setError('No token received. Please try again.')
            return false
          }
        }
      } else {
        const { data, status } = await axiosReq.post(
          '/app/v2/auth/2fa/reverify2fa',
          { totpCode: code, action: action || 'sensitive_action' }
        )

        if (status === 200) {
          const reverifyToken = data.data?.reverifyToken
          const expiresIn = data.data?.expiresIn
          onSuccess(reverifyToken, expiresIn, undefined)
          onClose()
          return true
        }
      }

      return false
    } catch (error: any) {
      if (error.response?.status === 423) {
        setError(
          error.response?.data?.message ||
            'Account locked. Please try again later.'
        )
      } else if (error.response?.status === 401) {
        setError('Invalid verification code. Please try again.')
      } else {
        setError('Verification failed. Please try again.')
      }
      return false
    } finally {
      setIsVerifying(false)
    }
  }

  const handleVerifyRecovery = async (): Promise<boolean> => {
    if (purpose === 'login' && !preAuthToken) {
      setError('Session expired. Please log in again.')
      return false
    }

    if (!recoveryCode || recoveryCode.length !== 6) {
      setError('Please enter a valid 6-digit code!')
      return false
    }

    setIsVerifyingRecovery(true)
    setError('')

    try {
      const { data, status } = await axiosReq.post(
        '/app/v2/auth/2fa/recovery/verify',
        { method: recoveryMethod, code: recoveryCode },
        { headers: { Authorization: `Bearer ${preAuthToken}` } }
      )

      if (status === 200) {
        const token = data.data?.token
        const requiresAuthenticatorSetup =
          data.data?.requiresAuthenticatorSetup ?? true

        if (token) {
          localStorage.setItem('token', token)
          localStorage.removeItem('preAuthToken')
          const tempUser = localStorage.getItem('tempUser')
          if (tempUser) localStorage.setItem('uo', tempUser)
          localStorage.removeItem('tempUser')
          onSuccess(token, undefined, requiresAuthenticatorSetup)
          onClose()
          return true
        } else {
          setError('No token received. Please try again.')
          return false
        }
      }

      return false
    } catch (error: any) {
      setError(error.response?.data?.message || 'Invalid recovery code!')
      return false
    } finally {
      setIsVerifyingRecovery(false)
    }
  }

  const handleRequestRecovery = async (method: 'EMAIL' | 'SMS') => {
    if (purpose === 'login' && !preAuthToken) {
      setError('Session expired! Please log in again.')
      return
    }

    if (method === 'EMAIL') setIsRequestingEmail(true)
    else setIsRequestingSMS(true)
    setError('')

    try {
      const { data, status } = await axiosReq.post(
        '/app/v2/auth/2fa/recovery/request',
        { method },
        { headers: { Authorization: `Bearer ${preAuthToken}` } }
      )

      if (status === 200) {
        setRecoveryMethod(method)
        setOtpRequested(true)
        setExpiresIn(data.expiresIn)
      }
    } catch (error: any) {
      if (error.response?.status === 429) {
        setError(
          error.response?.data?.message ||
            'Please wait before requesting another code.'
        )
      } else {
        setError(
          error.response?.data?.message ||
            `Failed to send recovery code via ${method}`
        )
      }
    } finally {
      if (method === 'EMAIL') setIsRequestingEmail(false)
      else setIsRequestingSMS(false)
    }
  }

  const handleResend = async () => {
    if (purpose === 'login' && !preAuthToken) {
      setError('Session expired. Please log in again.')
      return
    }

    if (resendTimer > 0 || recoveryMethod == null) return

    setIsResending(true)
    setError('')

    try {
      const { data, status } = await axiosReq.post(
        '/app/v2/auth/2fa/recovery/request',
        { method: recoveryMethod },
        { headers: { Authorization: `Bearer ${preAuthToken}` } }
      )

      if (status === 200) {
        setExpiresIn(data.expiresIn)
        setRecoveryCode('')
        setError('')
      }
    } catch (error: any) {
      if (error.response?.status === 429) {
        setError(
          error.response?.data?.message ||
            'Please wait before requesting another code.'
        )
      } else {
        setError(
          error.response?.data?.message ||
            `Failed to resend recovery code via ${recoveryMethod}`
        )
      }
    } finally {
      setIsResending(false)
    }
  }

  const formatBackupCode = (value: string): string => {
    const strippedCode = value.toUpperCase().replace(/[\s-]/g, '').slice(0, 12)
    return strippedCode.replace(/(.{4})(.{0,4})(.{0,4})/, (_, a, b, c) =>
      [a, b, c].filter(Boolean).join('-')
    )
  }

  const getModalTitle = () => {
    if (purpose === 'login') return 'Two-Factor Authentication Required!'
    return 'Verify Identity'
  }

  const getModalSubtitle = () => {
    if (purpose === 'login') {
      if (useBackupCode)
        return 'Enter one of your backup codes to access your account.'
      if (otpRequested)
        return `Enter the 6-digit code sent to your ${recoveryMethod === 'EMAIL' ? 'email' : 'phone'}.`
      return 'Enter the 6-digit code from your authenticator app to complete login.'
    }
    if (otpRequested)
      return `Enter the 6-digit code sent to your ${recoveryMethod === 'EMAIL' ? 'email' : 'phone'}.`
    return 'Enter the 6-digit code from your authenticator app to verify your identity.'
  }

  const getModalDescription = () => {
    if (purpose === 'login') {
      return 'Two-factor authentication adds an extra layer of security to your account.'
    }
    return 'You are about to perform a sensitive action. Please verify your identity to proceed.'
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={getModalTitle()}
      subTitle={email && purpose === 'login' ? ` ${email}` : undefined}
      width="450px"
    >
      <div className="text-center mb-4">
        <div className="bg-primary bg-opacity-10 rounded-circle p-3 d-inline-flex mb-3 mx-auto">
          <Shield size={28} className="text-primary" />
        </div>
        <p className="text-secondary small mb-0">{getModalDescription()}</p>
      </div>

      <MessageAlert type="error" message={error} />

      {!otpRequested && (
        <>
          <div className="text-center mb-4">
            <h3 className="h5 fw-semibold mb-2">
              {useBackupCode ? 'Backup Code' : 'Verification Code'}
            </h3>
            <p className="text-secondary small mb-0">{getModalSubtitle()}</p>
          </div>

          <form onSubmit={handleSubmit}>
            <div className="mb-4">
              {useBackupCode && purpose === 'login' ? (
                <div className="mb-3">
                  <input
                    type="text"
                    className="form-control form-control-lg text-center"
                    placeholder="XXXX-XXXX-XXXX"
                    value={code}
                    onChange={(e) => setCode(formatBackupCode(e.target.value))}
                    maxLength={14}
                    autoFocus
                    disabled={isProcessing}
                    style={{ letterSpacing: '0.1em', fontFamily: 'monospace' }}
                  />
                </div>
              ) : (
                <OtpInput value={code} onChange={setCode} />
              )}
            </div>

            <SpinnerButton
              type="submit"
              isLoading={isVerifying}
              className="btn btn-sm btn-primary w-100 py-2 mb-3"
              disabled={isProcessing || isLoginDisabled}
              buttonText={
                purpose === 'login' ? 'Verify & Login' : 'Verify & Continue'
              }
              loaderText="Verifying..."
            />

            {purpose === 'login' && (
              <button
                type="button"
                className="btn btn-sm btn-link w-100 text-decoration-none small text-info fw-light"
                onClick={() => {
                  setUseBackupCode(!useBackupCode)
                  setCode('')
                  setError('')
                }}
                disabled={isProcessing || isLoginDisabled}
              >
                <Key size={14} className="me-1" />
                {useBackupCode
                  ? 'Use authenticator app instead'
                  : 'Use a backup code instead'}
              </button>
            )}
          </form>
        </>
      )}

      {otpRequested && (
        <>
          <div className="text-center mb-4">
            <h3 className="h5 fw-semibold mb-2">Recovery Code</h3>
            <p className="text-secondary small mb-0">{getModalSubtitle()}</p>
          </div>

          <div className="bg-light rounded-3 p-3 mb-4 text-center">
            <small className="text-secondary d-block mb-1">
              Code expires in
            </small>
            <span className="badge bg-primary-subtle text-primary px-3 py-2">
              {Math.floor(expiryTime / 60)}m :{' '}
              {String(expiryTime % 60).padStart(2, '0')}s
            </span>
          </div>

          <div className="mb-4">
            <OtpInput
              value={recoveryCode}
              onChange={(val) => setRecoveryCode(val.slice(0, 6))}
            />
          </div>

          <div className="d-flex gap-2 pt-4">
            <button
              type="button"
              className="btn btn-sm btn-light font-sm flex-fill d-flex align-items-center justify-content-center gap-2"
              onClick={() => {
                setShowRecovery(false)
                setOtpRequested(false)
                setRecoveryCode('')
                setRecoveryMethod(null)
                setExpiresIn(0)
                setError('')
              }}
              disabled={isProcessing}
            >
              <ArrowLeft size={16} /> Back
            </button>

            <button
              type="button"
              className="btn btn-sm btn-light font-sm flex-fill d-flex align-items-center justify-content-center gap-2"
              onClick={handleResend}
              disabled={isProcessing || resendTimer > 0 || isLoginDisabled}
            >
              {isResending ? (
                <Loading size="sm" />
              ) : resendTimer > 0 ? (
                `Resend (${resendTimer}s)`
              ) : (
                'Resend'
              )}
            </button>

            <SpinnerButton
              isLoading={isVerifyingRecovery}
              onClick={handleVerifyRecovery}
              className="btn btn-primary font-sm flex-fill"
              disabled={
                recoveryCode.length !== 6 || isProcessing || isLoginDisabled
              }
              buttonText="Verify & Login"
              loaderText="Verifying..."
            />
          </div>
        </>
      )}

      {!otpRequested && !useBackupCode && purpose === 'login' && (
        <>
          <hr className="my-4" />
          <button
            type="button"
            className="btn btn-sm btn-link w-100 text-decoration-none small text-info fw-light"
            onClick={() => setShowRecovery(!showRecovery)}
            disabled={isProcessing}
          >
            Unable to access your authenticator?
          </button>

          {showRecovery && (
            <div className="mt-3 p-3 bg-light rounded">
              <p className="small text-secondary mb-3">
                Receive a recovery code via:
              </p>
              <div className="d-flex gap-2 mb-1">
                <button
                  type="button"
                  className="btn btn-outline-success btn-sm flex-fill font-sm d-flex align-items-center justify-content-center gap-2"
                  onClick={() => handleRequestRecovery('EMAIL')}
                  disabled={isProcessing || isLoginDisabled}
                >
                  {isRequestingEmail ? (
                    <Loading size="sm" />
                  ) : (
                    <Mail size={14} />
                  )}
                  Email
                </button>
                <button
                  type="button"
                  className="btn btn-outline-success btn-sm flex-fill font-sm d-flex align-items-center justify-content-center gap-2"
                  onClick={() => handleRequestRecovery('SMS')}
                  disabled={isProcessing || isLoginDisabled}
                >
                  {isRequestingSMS ? (
                    <Loading size="sm" />
                  ) : (
                    <Phone size={14} />
                  )}
                  SMS
                </button>
              </div>
            </div>
          )}
        </>
      )}

      <hr className="my-4" />

      <div className="alert alert-warning py-2 px-3 mb-0 small">
        <div className="d-flex gap-2">
          <AlertTriangle
            size={18}
            className="text-warning flex-shrink-0 mt-1"
          />
          <div>
            <p className="fw-semibold mb-1 small text-dark">Important!</p>
            <p className="mb-0 small">
              {purpose === 'login'
                ? 'If you lose access to your authenticator app, use one of your backup codes or request a recovery code via email/SMS.'
                : 'For security reasons, this verification expires in 5 minutes.'}
            </p>
          </div>
        </div>
      </div>
    </Modal>
  )
}

export default TwoFactorVerificationModal
